/**
 * The values currently filtering a list, each with its own way out.
 *
 * ── WHY THIS EXISTS RATHER THAN A CHIP ROW ──────────────────────────────────
 *
 * The app has exactly one filter control — `.chip` — and the feed and Wiki One
 * both use it as a row of every option (FeedFilter, Wiki's CuratorBar). That
 * works when the options are four or five and fails at sixty-nine org units,
 * which is why the administration screen picks with a `<select>` instead.
 *
 * A select hides both the current value and the alternatives behind a click,
 * and Wiki.tsx already wrote down why that is bad: "the commonest confusion
 * with a filtered list is not knowing it is filtered". So the picker stays a
 * select and the CHOICES come back out as chips underneath it — the same
 * `.chip` the rest of the app uses, not a second vocabulary.
 *
 * ── THE × IS SANCTIONED, AND IT IS THE ONLY ONE ─────────────────────────────
 *
 * The design system's `Tag` carries `onRemove` as a transparent button at
 * opacity .7, and its readme permits `×` for dismiss, `↓` for download and `⌕`
 * for search — nothing else. Everywhere else in this app removal is a worded
 * button: "Remove" beside a role grant, "Clear filters", "Dismiss" in the
 * moderation queue. Those stay worded because they act on a RECORD; this acts
 * on the label you are already looking at, where a word would be longer than
 * the thing it removes.
 *
 * ── PILL, DECIDED (OPS-32 → D3, David, 11 Sep) ─────────────────────────────
 *
 * The design system said a chip is `--radius-sm` and the switch is the only
 * pill; the app made every `.chip` a pill. David chose pills: in this app a
 * pill means "a filter you can press", and a rectangle means "a fact you read"
 * (the badge recipe in hub.css). The design system's Tag spec is the thing to
 * update, not this. Chips are 28px (`--control-h-xs`) — this row uses the
 * plain `.chip`, and the clear button is `.btn--xs` so the row is one height.
 */
export type FilterChip = {
  /** Stable within its group; passed back to onRemove. */
  value: string
  /** What the person chose — a BU name, a role name, a status. */
  label: string
  /** Which filter it belongs to. Without it every chip reads as one list. */
  group: string
}

export function FilterChips({
  chips,
  onRemove,
  onClear,
}: {
  chips: FilterChip[]
  onRemove: (chip: FilterChip) => void
  onClear: () => void
}) {
  if (chips.length === 0) return null

  return (
    <div className="chips" role="group" aria-label="Filters applied">
      {chips.map((c) => (
        // A span, not a button. The chip is a label that happens to carry one
        // control; making the chip itself pressable would put a button inside
        // a button and leave "what does clicking the middle do" unanswered.
        <span key={`${c.group}:${c.value}`} className="chip chip--value">
          <span className="chip__of">{c.group}</span>
          {c.label}
          <button
            type="button"
            className="chip__x"
            aria-label={`Remove ${c.group} filter ${c.label}`}
            onClick={() => onRemove(c)}
          >
            ×
          </button>
        </span>
      ))}
      {/* Kept as a word. Clearing everything is a different act from removing
          one thing, and giving it its own × would make two glyphs mean two
          different sizes of the same verb. */}
      <button type="button" className="btn btn--quiet btn--xs" onClick={onClear}>
        Clear filters
      </button>
    </div>
  )
}
